import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faPlus,
  faEdit,
  faTrash,
  faChartLine,
  faCheck,
  faTimes,
  faSortUp,
  faSortDown,
  faSort,
  faPlay,
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';
import '../styles/AdminStatus.css';

const AdminStatus = () => {
  const [statuses, setStatuses] = useState([]);
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);

  // Load statuses from API
  const loadStatuses = async () => {
    try {
      setLoading(true);
      const result = await apiService.getStatuses();
      if (result.success) {
        setStatuses(result.data);
      } else {
        console.error('Failed to load statuses:', result.error);
      }
    } catch (error) {
      console.error('Error loading statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterAndSortStatuses();
  }, [searchTerm, sortField, sortDirection, statuses]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterAndSortStatuses = () => {
    let filtered = [...statuses];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(status =>
        status.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        status.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredStatuses(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddStatus = () => {
    setEditingStatus(null);
    setShowAddModal(true);
  };

  const handleEditStatus = (status) => {
    setEditingStatus(status);
    setShowAddModal(true);
  };

  const handleDeleteStatus = async (statusId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa trạng thái này?')) {
      setLoading(true);
      try {
        const result = await apiService.deleteStatus(statusId);
        if (result.success) {
          setStatuses(statuses.filter(status => status.id !== statusId));
        } else {
          alert('Lỗi khi xóa trạng thái: ' + result.error);
        }
      } catch (error) {
        alert('Lỗi khi xóa trạng thái: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleStatus = async (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    const newStatus = status.status === 'active' ? 'inactive' : 'active';
    
    setLoading(true);
    try {
      const result = await apiService.updateStatus(statusId, { status: newStatus });
      if (result.success) {
        setStatuses(statuses.map(stat => 
          stat.id === statusId 
            ? { ...stat, status: newStatus }
            : stat
        ));
      } else {
        alert('Lỗi khi cập nhật trạng thái: ' + result.error);
      }
    } catch (error) {
      alert('Lỗi khi cập nhật trạng thái: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStatus = async (statusData) => {
    setLoading(true);
    try {
      if (editingStatus) {
        // Update existing status
        const result = await apiService.updateStatus(editingStatus.id, statusData);
        if (result.success) {
          setStatuses(statuses.map(status => 
            status.id === editingStatus.id 
              ? result.data
              : status
          ));
        } else {
          alert('Lỗi khi cập nhật trạng thái: ' + result.error);
          return;
        }
      } else {
        // Add new status
        const result = await apiService.createStatus(statusData);
        if (result.success) {
          setStatuses([...statuses, result.data]);
        } else {
          alert('Lỗi khi tạo trạng thái: ' + result.error);
          return;
        }
      }
      setShowAddModal(false);
      setEditingStatus(null);
    } catch (error) {
      alert('Lỗi khi lưu trạng thái: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowAddModal(false);
    setEditingStatus(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Hoạt động';
      case 'inactive': return 'Tạm dừng';
      default: return 'Không xác định';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const getIconByName = (iconName) => {
    const iconMap = {
      faPlay: faPlay,
      faCheckCircle: faCheckCircle,
      faTimesCircle: faTimesCircle,
      faChartLine: faChartLine
    };
    return iconMap[iconName] || faChartLine;
  };

  if (loading) {
    return (
      <div className="admin-status">
        <div className="status-loading">
          <div className="loading-spinner"></div>
          <p>Đang tải danh sách trạng thái...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-status">
      <div className="status-header">
        <div className="header-left">
          <h1>
            <FontAwesomeIcon icon={faChartLine} />
            Quản lý Trạng thái
          </h1>
          <p>Quản lý các trạng thái phim (Ongoing, Complete, Drop)</p>
        </div>
        <div className="header-actions">
          <button className="add-status-btn" onClick={handleAddStatus}>
            <FontAwesomeIcon icon={faPlus} />
            Thêm trạng thái
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="status-stats">
        <div className="stat-card">
          <div className="stat-icon success">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{statuses.filter(s => s.status === 'active').length}</div>
            <div className="stat-label">Trạng thái hoạt động</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">
            <FontAwesomeIcon icon={faTimes} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{statuses.filter(s => s.status === 'inactive').length}</div>
            <div className="stat-label">Trạng thái tạm dừng</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon primary">
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{statuses.reduce((sum, s) => sum + s.movie_count, 0)}</div>
            <div className="stat-label">Tổng phim</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon info">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{statuses.length}</div>
            <div className="stat-label">Tổng trạng thái</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="status-filters">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Tìm kiếm trạng thái..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Status Table */}
      <div className="status-table-container">
        <table className="status-table">
          <thead>
            <tr>
              <th>Icon</th>
              <th onClick={() => handleSort('name')} className="sortable">
                Tên trạng thái
                <FontAwesomeIcon 
                  icon={sortField === 'name' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th>Mô tả</th>
              <th>Màu sắc</th>
              <th onClick={() => handleSort('movie_count')} className="sortable">
                Số phim
                <FontAwesomeIcon 
                  icon={sortField === 'movie_count' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('display_order')} className="sortable">
                Thứ tự
                <FontAwesomeIcon 
                  icon={sortField === 'display_order' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Trạng thái
                <FontAwesomeIcon 
                  icon={sortField === 'status' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('created_at')} className="sortable">
                Ngày tạo
                <FontAwesomeIcon 
                  icon={sortField === 'created_at' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredStatuses.map(status => (
              <tr key={status.id}>
                <td>
                  <div className="status-icon" style={{ color: status.color }}>
                    <FontAwesomeIcon icon={getIconByName(status.icon)} />
                  </div>
                </td>
                <td>
                  <div className="status-info">
                    <div className="status-name">{status.name}</div>
                    <div className="status-slug">/{status.slug}</div>
                  </div>
                </td>
                <td>
                  <div className="status-description">
                    {status.description}
                  </div>
                </td>
                <td>
                  <div className="color-preview">
                    <div 
                      className="color-box" 
                      style={{ backgroundColor: status.color }}
                    ></div>
                    <span className="color-code">{status.color}</span>
                  </div>
                </td>
                <td>
                  <div className="movie-count">
                    <span className="count-number">{status.movie_count}</span>
                    <span className="count-label">phim</span>
                  </div>
                </td>
                <td>
                  <div className="display-order">
                    <span className="order-number">{status.display_order}</span>
                  </div>
                </td>
                <td>
                  <div className={`status-badge ${getStatusColor(status.status)}`}>
                    {getStatusText(status.status)}
                  </div>
                </td>
                <td>
                  <div className="created-date">
                    {formatDate(status.created_at)}
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className={`action-btn toggle ${status.status}`}
                      title={status.status === 'active' ? 'Tạm dừng' : 'Kích hoạt'}
                      onClick={() => handleToggleStatus(status.id)}
                    >
                      <FontAwesomeIcon icon={status.status === 'active' ? faTimes : faCheck} />
                    </button>
                    <button 
                      className="action-btn edit"
                      title="Chỉnh sửa"
                      onClick={() => handleEditStatus(status)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="action-btn delete"
                      title="Xóa"
                      onClick={() => handleDeleteStatus(status.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Status Form Modal */}
      {showAddModal && (
        <StatusForm
          status={editingStatus}
          onSave={handleSaveStatus}
          onCancel={handleCancelEdit}
          isOpen={showAddModal}
        />
      )}
    </div>
  );
};

// Status Form Component
const StatusForm = ({ status, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    color: '#10b981',
    icon: 'faChartLine',
    display_order: 1,
    status: 'active'
  });

  const iconOptions = [
    { value: 'faPlay', label: 'Play (Đang phát)', icon: faPlay },
    { value: 'faCheckCircle', label: 'Check Circle (Hoàn thành)', icon: faCheckCircle },
    { value: 'faTimesCircle', label: 'Times Circle (Tạm dừng)', icon: faTimesCircle },
    { value: 'faChartLine', label: 'Chart Line (Khác)', icon: faChartLine }
  ];

  useEffect(() => {
    if (status) {
      setFormData({
        name: status.name || '',
        slug: status.slug || '',
        description: status.description || '',
        color: status.color || '#10b981',
        icon: status.icon || 'faChartLine',
        display_order: status.display_order || 1,
        status: status.status || 'active'
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        color: '#10b981',
        icon: 'faChartLine',
        display_order: 1,
        status: 'active'
      });
    }
  }, [status]);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    
    // Auto-generate slug when name changes
    if (name === 'name') {
      newFormData.slug = generateSlug(value);
    }
    
    setFormData(newFormData);
  };

  const getIconByValue = (iconValue) => {
    const option = iconOptions.find(opt => opt.value === iconValue);
    return option ? option.icon : faChartLine;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content status-form-modal">
        <div className="modal-header">
          <h3>
            <FontAwesomeIcon icon={faChartLine} />
            {status ? 'Chỉnh sửa trạng thái' : 'Thêm trạng thái mới'}
          </h3>
          <button className="modal-close" onClick={onCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="status-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Tên trạng thái *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="VD: Đang phát sóng"
                required
              />
            </div>

            <div className="form-group">
              <label>Slug *</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="ongoing"
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Mô tả</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Mô tả về trạng thái này..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Màu sắc</label>
              <div className="color-input-group">
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  placeholder="#10b981"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Icon</label>
              <select name="icon" value={formData.icon} onChange={handleChange}>
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="icon-preview">
                <FontAwesomeIcon 
                  icon={getIconByValue(formData.icon)} 
                  style={{ color: formData.color }}
                />
                <span>Preview</span>
              </div>
            </div>

            <div className="form-group">
              <label>Thứ tự hiển thị</label>
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                min="1"
              />
            </div>

            <div className="form-group">
              <label>Trạng thái</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Hoạt động</option>
                <option value="inactive">Tạm dừng</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              <FontAwesomeIcon icon={faTimes} />
              Hủy
            </button>
            <button type="submit" className="btn-save">
              <FontAwesomeIcon icon={faCheck} />
              {status ? 'Cập nhật' : 'Thêm mới'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminStatus;